import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Box,
  Stack,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Layers, ChevronDown, Check, Search, X, Inbox } from 'lucide-react';

export const QueueSelectDropdown = ({
  selectedQueue = '',
  availableQueues = [],
  onSelectQueue = () => {},
  size = 'md',
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState('');

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const currentQueueObj = availableQueues.find((q) => q.name === selectedQueue);

  const filteredQueues = availableQueues.filter((q) =>
    q.name.toLowerCase().includes(search.toLowerCase().trim()),
  );

  return (
    <Box sx={{ display: 'inline-block' }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={
          <ChevronDown
            style={{
              width: 16,
              height: 16,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
          />
        }
        startIcon={
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: 1,
              bgcolor: 'rgba(0, 105, 81, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#006951',
            }}
          >
            <Layers style={{ width: 14, height: 14 }} />
          </Box>
        }
        sx={{
          borderColor: 'rgba(0, 105, 81, 0.3)',
          bgcolor: 'rgba(0, 105, 81, 0.04)',
          color: '#006951',
          py: size === 'lg' ? 1 : size === 'sm' ? 0.4 : 0.7,
          px: 1.8,
          borderRadius: 1,
          fontWeight: 700,
          fontFamily: 'monospace',
          fontSize: size === 'lg' ? '1.05rem' : size === 'sm' ? '0.75rem' : '0.9rem',
          '&:hover': {
            borderColor: '#006951',
            bgcolor: 'rgba(0, 105, 81, 0.08)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>{selectedQueue || 'Select Queue'}</span>
          {currentQueueObj && (
            <Chip
              label={`${currentQueueObj.messages} msgs`}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: 'rgba(0, 105, 81, 0.12)',
                color: '#006951',
                borderRadius: 1,
              }}
            />
          )}
        </Stack>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: 280, sm: 360 },
            maxHeight: 380,
            borderRadius: 1.5,
            boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
            p: 1,
          },
        }}
      >
        {/* Search input */}
        {availableQueues.length > 2 && (
          <Box sx={{ p: 1, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search queues..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ width: 14, height: 14, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <X style={{ width: 14, height: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: { borderRadius: 2, fontSize: '0.8rem' },
              }}
            />
          </Box>
        )}

        {/* Queues list */}
        {filteredQueues.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Inbox style={{ width: 24, height: 24, margin: '0 auto 6px', color: '#94a3b8' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              No queues matching &quot;{search}&quot;
            </Typography>
          </Box>
        ) : (
          filteredQueues.map((q, idx) => {
            const isSelected = q.name === selectedQueue;
            return (
              <MenuItem
                key={`${q.vhost || '/'}-${q.name}-${idx}`}
                onClick={() => {
                  onSelectQueue(q.name);
                  handleClose();
                }}
                selected={isSelected}
                sx={{
                  borderRadius: 2,
                  py: 1,
                  px: 1.5,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0, 105, 81, 0.08)',
                    color: '#006951',
                    fontWeight: 700,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1.5,
                        bgcolor: isSelected ? '#006951' : '#f1f5f9',
                        color: isSelected ? '#fff' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Layers style={{ width: 14, height: 14 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontFamily: 'monospace',
                          fontWeight: isSelected ? 700 : 500,
                          fontSize: '0.85rem',
                        }}
                      >
                        {q.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                        vhost: {q.vhost || '/'} {q.state ? `• ${q.state}` : ''}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, ml: 1 }}>
                    <Chip
                      label={`${q.messages} ${q.messages === 1 ? 'msg' : 'msgs'}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: q.messages > 0 ? 'rgba(217, 119, 6, 0.12)' : '#f1f5f9',
                        color: q.messages > 0 ? '#d97706' : '#64748b',
                      }}
                    />
                    {isSelected && <Check style={{ width: 14, height: 14, color: '#006951' }} />}
                  </Stack>
                </Stack>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
};
